import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check, ArrowRight } from "@phosphor-icons/react";
import Seo, { breadcrumbs, SITE_URL } from "@/lib/seo";

const FAQ = [
  ["Do you take any royalty cut?", "No. Ever. You pay one flat annual fee and keep 100% of what your music earns on every platform."],
  ["When does my music go live?", "Once approved by our A&R team, releases hit stores in 2-7 days. Pro and Label tiers get priority review (24-48h). Schedule 2-4 weeks ahead for Spotify playlist consideration."],
  ["Can I cancel anytime?", "Yes. Your subscription won't auto-renew. Music already released stays live as long as your account remains active."],
  ["What platforms do you distribute to?", "150+ streaming and social platforms including Spotify, Apple Music, TIDAL, Amazon Music, YouTube Music, TikTok, Instagram, Deezer, Pandora, and more."],
  ["Is Asylum cheaper than DistroKid or TuneCore?", "Starter is $9.99/year for one release and Pro is $29.99/year for unlimited releases — less than TuneCore's unlimited plan and in line with DistroKid, with a human A&R review and label-name releases included."],
  ["Will my release show a label name on Spotify and Apple Music?", "Yes. Every release is delivered with 'Asylum Entertainment' as the label credit, while you keep full ownership of your masters."],
  ["Can I upload albums and EPs?", "Yes. Starter is singles only; Pro allows up to 8 tracks per release; Label has no track limit. WAV, FLAC, MP3 and M4A are accepted."],
  ["How do I get paid?", "Store royalties appear in your dashboard per platform and release. Once your balance reaches $50 you can request a PayPal payout from your dashboard."],
  ["What does the Label tier include?", "Unlimited sub-artist accounts on your roster, per-release royalty splits that each artist sees on their own dashboard, unlimited releases, 24-hour review and a dedicated A&R contact."],
  ["Do I need ISRC or UPC codes?", "No. They're assigned during delivery at no cost. If you already own ISRCs you can enter them when you upload."],
];

export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/pricing/tiers").then(({ data }) => setTiers(data.tiers));
  }, []);

  const subscribe = async (lookup_key) => {
    if (!user) { nav("/signup"); return; }
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const jsonLd = [
    breadcrumbs([["Home", "/"], ["Pricing", "/pricing"]]),
    { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    ...tiers.map((t) => ({
      "@type": "Product", name: `Asylum Entertainment ${t.name} plan`, description: `${t.tagline} ${t.features.join(", ")}.`, brand: { "@id": `${SITE_URL}/#organization` }, category: "Music distribution subscription",
      offers: { "@type": "Offer", price: t.price, priceCurrency: "USD", url: `${SITE_URL}/pricing`, availability: "https://schema.org/InStock", priceSpecification: { "@type": "UnitPriceSpecification", price: t.price, priceCurrency: "USD", billingDuration: 1, billingIncrement: 1, unitCode: "ANN" } },
    })),
  ];

  return (
    <>
      <Seo title="Pricing — Music Distribution from $9.99/year, 100% Royalties" path="/pricing"
        description="Asylum Entertainment plans: Starter $9.99/yr (1 release), Pro $29.99/yr (unlimited releases), Label $79.99/yr (unlimited artists, royalty splits). Distribute to Spotify, Apple Music and 150+ stores and keep 100% of your royalties." jsonLd={jsonLd} />
      <Navbar />
      <section className="border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-4" data-testid="pricing-eyebrow">— Pricing</div>
          <h1 className="font-display text-5xl lg:text-7xl uppercase tracking-tighter leading-[0.9] mb-6 max-w-4xl">
            Pick your <span className="text-red-600">weight class.</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl">
            One flat fee. Zero royalty splits. Cancel anytime. Every plan pushes your music to every major streaming service on Earth.
          </p>
        </div>
      </section>

      <section className="border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <div className="grid md:grid-cols-3 gap-0 border border-zinc-800">
            {tiers.map((t, i) => (
              <div key={t.id} className={`p-8 lg:p-10 flex flex-col border-r border-zinc-800 last:border-r-0 ${t.popular ? "bg-zinc-950" : ""} relative`} data-testid={`tier-card-${t.id}`}>
                {t.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-red-600 text-white text-[10px] tracking-[0.3em] uppercase py-1 text-center">
                    Most Popular
                  </div>
                )}
                <div className={`${t.popular ? "mt-6" : ""}`}>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-3">— Tier 0{i+1}</div>
                  <h3 className="font-display text-4xl uppercase tracking-tighter mb-2">{t.name}</h3>
                  <p className="text-sm text-zinc-500 mb-8">{t.tagline}</p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-display text-6xl tracking-tighter">${t.price}</span>
                    <span className="text-xs tracking-[0.2em] uppercase text-zinc-500">/ year</span>
                  </div>
                  <div className="text-xs text-zinc-600 mb-8">Billed annually. Cancel anytime.</div>
                </div>
                <ul className="space-y-3 flex-1 mb-8">
                  {t.features.map((f, idx) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check size={16} weight="bold" className="text-red-500 mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  disabled={busy === t.lookup_key}
                  onClick={() => subscribe(t.lookup_key)}
                  data-testid={`subscribe-btn-${t.id}`}
                  className={`w-full py-4 text-sm tracking-[0.2em] uppercase inline-flex items-center justify-center gap-3 btn-brutal ${t.popular ? "bg-red-600 text-white btn-brutal-red" : "border border-zinc-700 hover:border-white"} disabled:opacity-50`}
                >
                  {busy === t.lookup_key ? "Loading..." : "Get " + t.name}
                  <ArrowRight size={16} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <h2 className="font-display text-3xl uppercase tracking-tighter mb-10">FAQ</h2>
          <div className="grid md:grid-cols-2 gap-0 border border-zinc-800" data-testid="pricing-faq">
            {FAQ.map(([q, a], i) => (
              <div key={q} className="border-r border-b border-zinc-800 p-8">
                <h3 className="font-display text-lg uppercase mb-3">{q}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-sm text-zinc-500">Still comparing? Read <Link to="/compare" className="text-white underline underline-offset-4 hover:text-red-500" data-testid="pricing-compare-link">Asylum vs DistroKid vs TuneCore</Link> or see <Link to="/how-it-works" className="text-white underline underline-offset-4 hover:text-red-500">how it works</Link>.</div>
        </div>
      </section>
      <Footer />
    </>
  );
}
